import React, { useState, useEffect } from "react";
import ReactMapboxGl, { Marker } from "react-mapbox-gl";

import SearchAddress from "./SearchAddress";
import FindMyLocation from "./FindMyLocation";

import { mapirApiToken } from "config";
import { useSelector } from "react-redux";
import { mapboxAccessToken } from "config";
import { useGeolocation } from "hooks/geoHook";
import { Box, Card, Stack, styled, TextField } from "@mui/material";
import { setRTLTextPlugin, getRTLTextPluginStatus } from "mapbox-gl";

// import "./styles.css";
import "mapbox-gl/dist/mapbox-gl.css";

const Map = ReactMapboxGl({
  minZoom: 6,
  maxZoom: 20,
  bearingSnap: 10,
  renderWorldCopies: false,
  attributionControl: false,
  accessToken: mapboxAccessToken,
  // interactive: !disabled,
});

const LocationFieldTemplate = (props) => {
  const {
    formData,
    onChange,
    disabled,
    zoom = [15],
    width = "100%",
    height = "280px",
  } = props;
  const state = useGeolocation();
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState([state.longitude, state.latitude]);
  const { mode } = useSelector((state) => state.setting);

  const [formState, setFormState] = useState({
    errors: {},
    values: {},
    touched: {},
    isValid: false,
  });

  const style =
    mode === "light" ? "/assets/map/green-gray.json" : "/assets/map/dark.json";

  const handleCenter = (lng, lat) => {
    setCenter([lng, lat]);
    reverseFunction({ lat, lng });
  };

  const handleMapClick = (map, event) =>
    handleCenter(event.lngLat.lng, event.lngLat.lat);

  const reverseFunction = ({ lat, lng }) => {
    var url = `https://map.ir/reverse/no?lat=${lat}&lon=${lng}`;
    fetch(url, {
      headers: {
        "x-api-key": mapirApiToken,
        "Mapir-SDK": "reactjs",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        const { city, county, country, address, province } = data;
        setFormState((formState) => {
          onChange({
            ...formState,
            values: {
              ...formData,
              ...formState.values,
              country,
              address,
              province,
              lat: String(lat),
              lng: String(lng),
              city: city || county,
            },
          });
          return {
            ...formState,
            values: {
              ...formData,
              ...formState.values,
              country,
              address,
              province,
              lat: String(lat),
              lng: String(lng),
              city: city || county,
            },
          };
        });
      });
  };

  const formatValue = (value) => {
    if (value) {
      return value
        .replace(/[^\d]/g, "") // فقط اعداد را می‌پذیرد
        .replace(/(\d{3})(\d)/, "$1-$2") // فرمت شماره
        .replace(/-(\d{3})(\d)/, "-$1-$2") // فرمت شماره
        .replace(/-(\d{4})/, "-$1"); // فرمت شماره
    }
    return value;
  };

  const handleChange = (event, index) => {
    const { value } = event.target;
    return setFormState((formState) => {
      onChange({ ...formState.values, [index]: value || "" });
      return {
        ...formState,
        values: {
          ...formState.values,
          [index]: value || "",
        },
        touched: {
          ...formState.touched,
          [index]: true,
        },
        errors: {
          ...formState.errors,
          [index]: false,
        },
      };
    });
  };

  const hasError = (index) =>
    formState.touched[index] && formState.errors[index] ? true : false;

  useEffect(() => {
    const status = getRTLTextPluginStatus();
    if (status === "unavailable") {
      setRTLTextPlugin("/assets/js/mapbox-gl-rtl-text.js");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData && loading) {
        handleCenter(
          formData?.lng || state.longitude,
          formData?.lat || state.latitude
        );
      }
      return setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [formData]);

  return (
    <Stack rowGap={1}>
      <Card variant="outlined">
        <Box
          width="100%"
          height="100%"
          overflow="hidden"
          bgcolor="#f9f9f9"
          position="relative"
          borderRadius={1}
        >
          <Map
            zoom={zoom}
            style={style}
            center={center}
            movingMethod="easeTo"
            onClick={handleMapClick}
            containerStyle={{ width, height }}
          >
            <Marker coordinates={center} anchor="center">
              <StyledBox
                width={16}
                height={16}
                borderRadius={1}
                sx={{ opacity: 0.9 }}
                color="primary"
                bgcolor="primary.main"
              />
            </Marker>
          </Map>
          <Stack
            p={0.5}
            top={0}
            left={0}
            right={0}
            zIndex={100}
            rowGap={0.5}
            position="absolute"
          >
            <SearchAddress
              changeCenter={(data) => handleCenter(data[0], data[1])}
            />
          </Stack>
          <Stack
            p={0.5}
            bottom={0}
            // left={0}
            right={0}
            zIndex={100}
            columnGap={0.5}
            position="absolute"
            alignItems="center"
            direction="row-reverse"
          >
            <FindMyLocation
              onClick={(data) => handleCenter(data[0], data[1])}
            />
          </Stack>
        </Box>
      </Card>
      <Stack rowGap={1}>
        <TextField
          fullWidth
          type="text"
          size="medium"
          variant="outlined"
          label="عنوان"
          onChange={(event) => handleChange(event, "title")}
          value={formState.values["title"] || ""}
          error={hasError("title")}
        />

        <TextField
          multiline
          minRows={3}
          fullWidth
          type="text"
          size="medium"
          variant="outlined"
          label="آدرس"
          onChange={(event) => handleChange(event, "address")}
          value={formState.values["address"] || ""}
          error={hasError("address")}
        />
        <Stack direction="row" columnGap={1}>
          <TextField
            fullWidth
            type="tel"
            size="medium"
            variant="outlined"
            label="تلفن"
            onChange={(event) => handleChange(event, "tellphone")}
            value={formatValue(formState.values["tellphone"]) || ""}
            error={hasError("tellphone")}
          />
          <TextField
            fullWidth
            type="text"
            size="medium"
            variant="outlined"
            label="کد پستی"
            onChange={(event) => handleChange(event, "postalCode")}
            value={formatValue(formState.values["postalCode"]) || ""}
            error={hasError("postalCode")}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const StyledBox = styled(Box)(({ theme, color = "primary" }) => ({
  "&::after": {
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    position: "absolute",
    border: "1px solid",
    animation: "ripple 1.2s infinite ease-in-out",
    borderColor: theme.palette[color].main,
    content: '""',
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

export default LocationFieldTemplate;
